import { isEmpty } from '@fxts/core';
import {
  CreatorProductData,
  MODE,
} from '@services/MarppleShopStudio/widget/CreatorProduct/core/types/types.ts';
import {
  validateEditFormData,
  validateEditTplFormData,
  validateEditTplIntlFormData,
  ValidateFormErrorKeyTypes,
  validateNewFormData,
  validationOptionalFormData,
  validationSkuFormData,
} from '@services/MarppleShopStudio/widget/CreatorProduct/core/store/helper.ts';
import { CreatorProductState } from '@services/MarppleShopStudio/widget/CreatorProduct/core/store/types.ts';
import { BaseForm } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/BaseForm.ts';
import { $ } from 'rune-ts';

/**
 * 폼 검증을 담당하는 서비스 클래스
 */
export class FormValidator<T extends CreatorProductData = CreatorProductData> {
  constructor(private form: BaseForm<T>) {}

  /**
   * 필수 폼 데이터 검증
   */
  validateByRequired() {
    return this.form.store.actions.validateForm();
  }

  /**
   * 모드별 폼 데이터 검증
   */
  validateByMode(): ValidateFormErrorKeyTypes[] {
    const validator = this.getValidatorForMode(this.form.store.mode);

    return validator(this.form.store.formData);
  }

  /**
   * 선택적 필드 검증
   */
  validateOptionalFields(): ValidateFormErrorKeyTypes[] {
    return validationOptionalFormData(this.form.store.formData);
  }

  /**
   * 썸네일 검증
   */
  validateThumbnails(): boolean {
    return isEmpty(this.form.thumbnailFormView.getFiles());
  }

  /**
   * 성인 상품 검증
   */
  validateAdult(): boolean {
    const need_adult_checked = $('html[need_adult_check="true"]');
    const need_certification = $(need_adult_checked)?.find('[need_certification="true"]');
    return Boolean(need_adult_checked && need_certification && this.form.store.formData.meta.adult);
  }

  /**
   * SKU 검증
   */
  validateSku(): ValidateFormErrorKeyTypes[] {
    return validationSkuFormData(this.form.store.formData);
  }

  /**
   * SKU 검증이 필요한지 확인
   */
  shouldValidateSku(createSku: boolean): boolean {
    return (
      (this.form.store.mode === MODE.EDIT_TPL || this.form.store.mode === MODE.EDIT_TPL_INTL) && createSku
    );
  }

  /**
   * 모드별 검증 함수 반환
   */
  private getValidatorForMode(mode: MODE) {
    const validators = {
      [MODE.NEW]: validateNewFormData,
      [MODE.EDIT]: validateEditFormData,
      [MODE.EDIT_TPL]: validateEditTplFormData,
      [MODE.EDIT_TPL_INTL]: (formData: typeof this.form.store.formData) =>
        this.isEditIntlMode(formData) ? validateEditTplIntlFormData(formData) : [],
    };

    return validators[mode] || (() => []);
  }

  /**
   * EDIT_TPL_INTL 모드인지 확인
   */
  private isEditIntlMode(
    formData: typeof this.form.store.formData,
  ): formData is CreatorProductState<typeof MODE.EDIT_TPL_INTL>['formData'] {
    return formData && this.form.store.mode === MODE.EDIT_TPL_INTL;
  }
}
